import { Box } from "@mui/material";
import { useIdleTimer } from "react-idle-timer";
import { Outlet } from "react-router-dom";
import { useAppDispatch } from "../../app/hooks";
import { useProtectedRoute } from "../login/useProtectedRoute";
import { NotificationWrapper } from "../../common/components/functional/notification/NotificationWrapper";
import OverlaySpinner from "../../common/components/functional/overlaySpinner/OverlaySpinner";
import { SnackbarWrapper } from "../../common/components/functional/snackbar/SnackbarWrapper";
import { signOut } from "../user/userSlice";
import { HomeDrawer } from "./HomeDrawer";
import { HomeHeaderSpacer, HomeMenu } from "./HomeMenu";
import { useCallback, useEffect, useRef } from "react";
import {
  EventsChannel,
  subscribeToChannel,
  unsubscribe,
} from "../../app/subscription";
import { handleMessagingSocketEvent } from "../messaging/handleMessagingSocketEvent";
import { MessagingEventPayload, SocketEvent } from "../messaging/types/socket";
import useMessage from "../../hooks/useMessage";
import { useTranslation } from "react-i18next";
import { store } from "../../app/store";
import useFetchInitialData from "../../hooks/useFetchInitialData";
import FloatingChatButton from "../messaging/FloatingChatButton";
import FloatingChatWindow from "../messaging/floating/FloatingChatWindow";
import { isMessagingSurfaceVisible } from "../messaging/useMessagingViewMode";
import { HOME_LAYOUT_HEIGHT } from "../../app/common";
import { DocumentTitle } from "../../app/documentTitle/DocumentTitle";
import {
  AnnouncementItem,
  SocketAnnouncementEvent,
} from "../settings/announcements/types";
import {
  EXPERT_ANNOUNCEMENT_CHANNEL,
  getAnnouncementSocketAction,
} from "../settings/announcements/announcementSocket";

export const HomeLayout = () => {
  const { user, isSessionLoading, loginRedirect } = useProtectedRoute();
  const dispatch = useAppDispatch();
  const { sendMessage } = useMessage();
  const { t } = useTranslation();
  const subscriptionsRef = useRef<Record<string, { unsub: () => void }>>({});

  const isMessagingPath = useCallback(() => {
    return isMessagingSurfaceVisible(
      window.location.pathname,
      store.getState().messaging.isFloatingWindowOpen,
    );
  }, []);

  const { isLocalizationCompleted, localizationError } = useFetchInitialData();

  // TODO: refactor to reusable hook or something
  useEffect(() => {
    if (!user?.id) return;

    const channelName = `messaging/user/${user.id}`;
    if (subscriptionsRef.current[channelName]) return;

    let isCancelled = false;
    let channelRef: EventsChannel;
    let subObj: { unsubscribe?: () => void };

    subscribeToChannel<SocketEvent<MessagingEventPayload>>(
      channelName,
      (event) => {
        if (isCancelled) return;

        handleMessagingSocketEvent(event, {
          dispatch,
          getState: store.getState,
          user,
          isMessagingPath,
          sendMessage,
          t,
        });
      },
      (err) => {
        console.error("Subscription error:", err);
      },
    )
      .then(({ channel, subscription }) => {
        if (isCancelled) {
          unsubscribe(channel, subscription);
          return;
        }
        channelRef = channel;
        subObj = subscription;
        subscriptionsRef.current[channelName] = {
          unsub: () => unsubscribe(channelRef, subObj),
        };
      })
      .catch((err) => console.error("Cannot connect to channel:", err));

    return () => {
      isCancelled = true;
      subscriptionsRef.current[channelName]?.unsub();
      // TODO: fix react warning
      // eslint-disable-next-line react-hooks/exhaustive-deps
      delete subscriptionsRef.current[channelName];
    };
    // TODO: fix missing dependencies
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  useEffect(() => {
    if (!user?.id) return;
    const channelName = EXPERT_ANNOUNCEMENT_CHANNEL;
    if (subscriptionsRef.current[channelName]) return;

    let isCancelled = false;
    let channelRef: EventsChannel;
    let subObj: { unsubscribe?: () => void };

    subscribeToChannel<SocketAnnouncementEvent<AnnouncementItem>>(
      channelName,
      (event) => {
        if (isCancelled) return;
        const action = getAnnouncementSocketAction(event);
        if (action) dispatch(action);
      },
    )
      .then(({ channel, subscription }) => {
        if (isCancelled) {
          unsubscribe(channel, subscription);
          return;
        }
        channelRef = channel;
        subObj = subscription;
        subscriptionsRef.current[channelName] = {
          unsub: () => unsubscribe(channelRef, subObj),
        };
      })
      .catch((err) => console.error("Cannot connect to channel:", err));
  }, [user?.id, dispatch]);

  /**
   * This function will fire when user has been idle for given amount in useIdleTimer timeout property
   */
  const onIdle = () => {
    dispatch(signOut());
  };

  const hourInMs = 60 * 60 * 1000;
  const isDev =
    window.location.hostname === "localhost" ||
    window.location.hostname === "127.0.0.1";

  useIdleTimer({
    onIdle,
    timeout: hourInMs,
    crossTab: true,
    syncTimers: 200,
    disabled: !user || isDev,
  });

  if (isSessionLoading) {
    return <OverlaySpinner isLoading />;
  }

  if (!user) {
    return loginRedirect;
  }

  return (
    <Box sx={{ display: "flex" }}>
      <DocumentTitle />
      <HomeMenu user={user} />
      <HomeDrawer />
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 2,
          maxHeight: HOME_LAYOUT_HEIGHT,
        }}
      >
        <HomeHeaderSpacer />
        <OverlaySpinner isLoading={!isLocalizationCompleted} />
        {isLocalizationCompleted && !localizationError && <Outlet />}
        <SnackbarWrapper />
        <NotificationWrapper />
        <FloatingChatWindow />
        <FloatingChatButton />
      </Box>
    </Box>
  );
};
